import { authApi } from './api';

export interface UserProfile {
    id: number;
    username?: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName?: string;
    phoneNumber?: string;
    profilePictureUrl?: string;
    userType?: string;
    roles?: string[];
    isActive: boolean;
    isStaff?: boolean;
    isSuperuser?: boolean;
    createdAt?: string;
    updatedAt?: string;
    dateJoined?: string;
    lastLogin?: string;
    groups?: string[];
}

export interface UpdateProfileData {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
}

export interface ChangePasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface UserSettings {
    emailNotifications: boolean;
    smsNotifications: boolean;
    theme: 'light' | 'dark' | 'auto';
    language: string;
    timezone: string;
}

class ProfileService {
    private baseUrl = '/auth'; // Routes through gateway to auth service

    // Get current user profile
    async getProfile(): Promise<UserProfile> {
        try {
            const response = await authApi.get(`${this.baseUrl}/profile`);
            // The API returns { success, message, data, errors }
            // We need the data field which contains the actual user profile
            return response.data.data;
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw error;
        }
    }

    // Update user profile
    async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
        try {
            const response = await authApi.put(`${this.baseUrl}/profile`, data);
            // The API returns { success, message, data, errors }
            return response.data.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }

    // Change password
    async changePassword(data: ChangePasswordData): Promise<void> {
        try {
            await authApi.post(`${this.baseUrl}/change-password`, data);
        } catch (error) {
            console.error('Error changing password:', error);
            throw error;
        }
    }

    // Upload profile picture
    async uploadProfilePicture(file: File): Promise<string> {
        try {
            const formData = new FormData();
            formData.append('profilePicture', file);

            const response = await authApi.post(`${this.baseUrl}/profile/upload-picture`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // The API returns { success, message, data: { profilePictureUrl }, errors }
            return response.data.data.profilePictureUrl;
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
        }
    }

    // Get user settings
    async getSettings(): Promise<UserSettings> {
        try {
            const response = await authApi.get(`${this.baseUrl}/settings`);
            return response.data;
        } catch (error) {
            console.error('Error fetching settings:', error);
            throw error;
        }
    }

    // Update user settings
    async updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
        try {
            const response = await authApi.put(`${this.baseUrl}/settings`, settings);
            return response.data;
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }

    // Get user activity log
    async getActivityLog(page: number = 1, limit: number = 10): Promise<{
        activities: Array<{
            id: number;
            action: string;
            description: string;
            timestamp: string;
            ipAddress?: string;
            userAgent?: string;
        }>;
        total: number;
        page: number;
        totalPages: number;
    }> {
        try {
            const response = await authApi.get(`${this.baseUrl}/activity-log`, {
                params: { page, limit }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching activity log:', error);
            throw error;
        }
    }

    // Delete user account (soft delete)
    async deleteAccount(password: string): Promise<void> {
        try {
            await authApi.post(`${this.baseUrl}/delete-account`, { password });
        } catch (error) {
            console.error('Error deleting account:', error);
            throw error;
        }
    }

    // Generate new API tokens
    async generateApiToken(name: string): Promise<{
        token: string;
        name: string;
        createdAt: string;
        expiresAt?: string;
    }> {
        try {
            const response = await authApi.post(`${this.baseUrl}/api-tokens`, { name });
            return response.data;
        } catch (error) {
            console.error('Error generating API token:', error);
            throw error;
        }
    }

    // Get user's API tokens
    async getApiTokens(): Promise<Array<{
        id: number;
        name: string;
        lastUsed?: string;
        createdAt: string;
        expiresAt?: string;
    }>> {
        try {
            const response = await authApi.get(`${this.baseUrl}/api-tokens`);
            return response.data;
        } catch (error) {
            console.error('Error fetching API tokens:', error);
            throw error;
        }
    }

    // Revoke API token
    async revokeApiToken(tokenId: number): Promise<void> {
        try {
            await authApi.delete(`${this.baseUrl}/api-tokens/${tokenId}`);
        } catch (error) {
            console.error('Error revoking API token:', error);
            throw error;
        }
    }
}

export const profileService = new ProfileService();
export default profileService;